import { useCallback, useEffect, useRef, useState } from 'react';

/*
 * SHOW MORE AS THE READER REACHES THE END — no button, no pager.
 *
 * Abu Hurayra has 1,825 reports and Muhammad 2,600. Rendered whole, each one is a citation, a
 * paragraph of Arabic, a translate control and a collapsible chain, so opening one panel built
 * tens of thousands of nodes before the reader had read a word. The list was always written
 * expecting this — `total` is a separate prop precisely because "what has loaded" and "how many
 * exist" were never meant to be the same number — and only the build shipped everything.
 *
 * Scroll rather than a "load more" button: reaching the end IS the request, and a control that
 * asks the reader to say what their scrolling already said is a click that buys nothing.
 *
 * WHERE THERE IS NO OBSERVER, SHOW EVERYTHING. A jsdom test, an old browser, a page printed to
 * PDF — in all of them nothing will ever fire the callback, and a list frozen at its first
 * twenty items with no way to reach the rest would be a feature that silently hides content.
 * Slow is a much better failure than incomplete.
 */
export interface GrowOnScroll {
  /** How many items to render now. */
  shown: number;
  /** Ref callback for the element that sits after the last item. */
  sentinel: (element: HTMLElement | null) => void;
}

export function useGrowOnScroll(total: number, step: number): GrowOnScroll {
  const supported = typeof IntersectionObserver !== 'undefined';
  const [shown, setShown] = useState(() => (supported ? Math.min(step, total) : total));
  const observer = useRef<IntersectionObserver | null>(null);

  /*
   * A shrinking total means a different figure's panel — start again rather than keep a count from
   * the last one, which would open the next panel already scrolled.
   *
   * ADJUSTED DURING RENDER RATHER THAN IN AN EFFECT, AND THAT ONE RENDER'S DELAY WAS A REAL DEFECT.
   *
   * These lists mount while their content is still being fetched, so the initialiser above runs with
   * `total: 0` and `shown` starts at zero. An effect corrects it AFTER the commit — so for exactly
   * one commit the list renders empty while its own props say it is ready, and anything that reads
   * the DOM in a layout effect of that commit sees nothing at all.
   *
   * `ReadingDrawer` is that anything. It positions the reader on the verse it was asked to open by
   * finding `.verse--center` in a `useLayoutEffect`; it looked once, at 297ms, into a body holding
   * nought verses, found null, and never looked again because its dependencies had not changed. So
   * "Open Matthew 27:5 on the map" left the reader 1,351px above Matthew 27:5 — on every one of the
   * 3,213 `?passage=` links the static pages carry, which is how this library's readers arrive.
   *
   * Setting state during render is React's own documented answer to a prop the state must follow:
   * the re-render happens before the commit, so the list is populated in the same commit that says
   * it is ready, and there is no frame in which the two disagree.
   */
  const [countedFor, setCountedFor] = useState(total);
  if (countedFor !== total) {
    setCountedFor(total);
    setShown(supported ? Math.min(step, total) : total);
  }

  const sentinel = useCallback((element: HTMLElement | null) => {
    observer.current?.disconnect();
    observer.current = null;
    if (!element || !supported) return;
    const io = new IntersectionObserver(entries => {
      /* `some`, not `[0]`: an observer may deliver several entries at once, and the reader has
         reached the end if any of them says so. */
      if (entries.some(e => e.isIntersecting)) setShown(was => Math.min(was + step, total));
    });
    io.observe(element);
    observer.current = io;
  }, [step, total, supported]);

  useEffect(() => () => observer.current?.disconnect(), []);

  return { shown, sentinel };
}
